import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import ActivityDrawer from "@/components/ActivityDrawer";
import CreateActivityDialog from "@/components/CreateActivityDialog";
import { format } from "date-fns";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MapPin, Users, Clock, Link2, Copy, Check, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function MyActivities() {
    const [data, setData] = useState({ created: [], signed_up: [] });
    const [loading, setLoading] = useState(true);
    const [drawerId, setDrawerId] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editActivity, setEditActivity] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [ical, setIcal] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/activities/my");
            setData(data);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadIcal = useCallback(async () => {
        try {
            const { data } = await api.get("/ical/me");
            const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
            setIcal({
                token: data.token,
                personal_url: `${base}/api/ical/${data.token}/me.ics`,
                company_url: `${base}/api/ical/${data.token}/all.ics`,
            });
        } catch { /* empty */ }
    }, []);

    useEffect(() => { load(); loadIcal(); }, [load, loadIcal]);

    const buildUrls = (token) => {
        const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
        return {
            personal_url: `${base}/api/ical/${token}/me.ics`,
            company_url: `${base}/api/ical/${token}/all.ics`,
        };
    };

    const regenerate = async () => {
        if (!confirm("Regenerate iCal links? Old subscription URLs will stop working.")) return;
        try {
            const { data } = await api.post("/ical/me/regenerate");
            setIcal({ token: data.token, ...buildUrls(data.token) });
            toast.success("New iCal links generated");
        } catch {
            toast.error("Failed to regenerate");
        }
    };

    const open = (a) => { setDrawerId(a.id); setDrawerOpen(true); };

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 max-w-[1100px] w-full mx-auto px-6 lg:px-10 py-8 lg:py-12">
                <div className="mb-8">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2">Personal</p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-[#111110]" data-testid="my-activities-title">My activities</h1>
                    <p className="text-[#666664] text-sm mt-2">Everything you&apos;ve created or joined.</p>
                </div>

                {ical && (
                    <section className="border border-[#E5E5E3] bg-white p-6 mb-10" data-testid="ical-panel">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="max-w-xl">
                                <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">Subscribe</p>
                                <h2 className="font-heading text-xl font-medium text-[#111110] mt-1">Sync to your calendar app</h2>
                                <p className="text-sm text-[#666664] mt-2 leading-relaxed">
                                    Add either link to Google Calendar, Apple Calendar, or Outlook to see scratchfirst events alongside your own. Updates automatically.
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                onClick={regenerate}
                                data-testid="regenerate-ical-btn"
                                className="rounded-none border-[#E5E5E3] h-9 text-[#111110] hover:bg-[#F0F0EE] text-xs tracking-[0.15em] uppercase"
                            >
                                <RefreshCcw className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Regenerate
                            </Button>
                        </div>
                        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <CopyableUrl label="Personal — only events I've signed up for" url={ical.personal_url} testId="ical-personal" />
                            <CopyableUrl label="Company — every scratchfirst activity" url={ical.company_url} testId="ical-company" />
                        </div>
                    </section>
                )}

                <Tabs defaultValue="signed" className="w-full">
                    <TabsList className="bg-transparent p-0 border-b border-[#E5E5E3] rounded-none w-full justify-start h-auto" data-testid="my-tabs">
                        <TabsTrigger value="signed" data-testid="tab-signed" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-[#111110] data-[state=active]:border-b-2 data-[state=active]:border-[#111110] data-[state=active]:shadow-none text-[#666664] text-sm px-4 py-3 -mb-px">
                            Signed up ({data.signed_up.length})
                        </TabsTrigger>
                        <TabsTrigger value="created" data-testid="tab-created" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-[#111110] data-[state=active]:border-b-2 data-[state=active]:border-[#111110] data-[state=active]:shadow-none text-[#666664] text-sm px-4 py-3 -mb-px">
                            Created ({data.created.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="signed" className="mt-8">
                        {loading ? <Loading /> : <ActivityList list={data.signed_up} onClick={open} emptyText="You haven't signed up for any activities yet." />}
                    </TabsContent>
                    <TabsContent value="created" className="mt-8">
                        {loading ? <Loading /> : <ActivityList list={data.created} onClick={open} emptyText="You haven't created any activities yet." />}
                    </TabsContent>
                </Tabs>
            </main>

            <ActivityDrawer
                activityId={drawerId}
                open={drawerOpen}
                onOpenChange={setDrawerOpen}
                onChanged={load}
                onEdit={(a) => { setDrawerOpen(false); setEditActivity(a); setDialogOpen(true); }}
            />
            <CreateActivityDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                activity={editActivity}
                onSaved={load}
            />
        </div>
    );
}

function CopyableUrl({ label, url, testId }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Could not copy");
        }
    };
    return (
        <div className="border border-[#E5E5E3]" data-testid={testId}>
            <div className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[#888885] border-b border-[#E5E5E3] bg-[#FAFAF9]">{label}</div>
            <div className="flex items-center">
                <span className="px-3 text-[#666664]"><Link2 className="w-4 h-4" strokeWidth={1.5} /></span>
                <input
                    readOnly
                    value={url}
                    onClick={e => e.target.select()}
                    data-testid={`${testId}-input`}
                    className="flex-1 bg-transparent border-none outline-none text-xs text-[#111110] font-mono py-2.5 pr-2 truncate"
                />
                <button onClick={copy} data-testid={`${testId}-copy-btn`} className="px-3 h-10 border-l border-[#E5E5E3] text-xs tracking-[0.15em] uppercase font-medium text-[#111110] hover:bg-[#F0F0EE] inline-flex items-center gap-1.5">
                    {copied ? <Check className="w-3.5 h-3.5" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>
        </div>
    );
}

function Loading() {
    return <div className="border border-[#E5E5E3] bg-white p-16 text-center text-[#888885] text-sm tracking-[0.15em] uppercase">Loading…</div>;
}

function ActivityList({ list, onClick, emptyText }) {
    if (!list.length) {
        return (
            <div className="border border-[#E5E5E3] bg-white p-16 text-center">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-3">Empty</p>
                <p className="font-heading text-2xl font-light text-[#111110]">{emptyText}</p>
            </div>
        );
    }
    return (
        <ul className="border border-[#E5E5E3] bg-white divide-y divide-[#E5E5E3]" data-testid="my-activities-list">
            {list.map(a => (
                <li key={a.id}>
                    <button onClick={() => onClick(a)} className="w-full text-left p-6 md:p-8 hover:bg-[#FAFAF9] transition-colors" data-testid={`my-activity-${a.id}`}>
                        {a.tag && <div className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2">{a.tag}</div>}
                        <div className="flex items-start justify-between gap-4">
                            <h3 className="font-heading text-xl font-medium text-[#111110]">{a.title}</h3>
                            <span className="shrink-0 text-xs text-[#666664] tabular-nums tracking-wide">
                                {format(new Date(a.start_time), "EEE, MMM d · HH:mm")}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-[#666664]">
                            {a.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />{a.location}</span>}
                            <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" strokeWidth={1.5} />{a.signup_count}{a.max_signups ? `/${a.max_signups}`:""} signed up</span>
                            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} />{a.creator?.name}</span>
                        </div>
                    </button>
                </li>
            ))}
        </ul>
    );
}
